import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type NullablePartial } from '@yandex/ymaps3-types';
import { type Nullable } from '@direct-frontend/stdlib';
import type { DrawerProps, DrawerRefs } from '../types';

type UseIsScrollingParams = Pick<DrawerProps, 'isOpen'> & NullablePartial<Pick<DrawerRefs, 'contentRef'>>;

export const useIsScrolling = ({ isOpen, contentRef }: UseIsScrollingParams) => {
    const [isScrolling, setIsScrolling] = useState<boolean>(false);

    const ref = useRef<Nullable<HTMLElement>>(null);

    const onScroll = useMemo(
        () => () => {
            if (ref.current) {
                const node = ref.current;
                const scrollTop = node.scrollTop;
                const maxScroll = node.scrollHeight - node.clientHeight;

                setIsScrolling(scrollTop >= 0 && scrollTop < maxScroll);
            }
        },
        []
    );

    const setScrollableNode = useCallback(
        (node: HTMLElement) => {
            if (ref.current === node) {
                return;
            }

            ref.current?.removeEventListener('scroll', onScroll);

            ref.current = node;

            ref.current.addEventListener('scroll', onScroll);
        },
        [onScroll]
    );

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const node = contentRef?.current || ref.current;

        if (node) {
            setScrollableNode(node);

            // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-288723
            onScroll();
        }

        return () => node?.removeEventListener('scroll', onScroll);
    }, [contentRef, isOpen, onScroll, setScrollableNode]);

    return {
        isScrolling,
        setScrollableNode,
    };
};
